<!-- ===== Page Header ===== -->
<div class="sce-page-hdr-shell">
  <div class="sce-page-hdr">
    <div class="sce-page-hdr-left">
      @if (!isProjectFormMode && skidData) {
        <h1 class="sce-skid-title">{{ getEquipmentDisplayTitle() }}</h1>
        <nav class="sce-breadcrumb">
          <button class="sce-bc-link" type="button" (click)="goBack()">Equipment</button>
          <i class="ri-arrow-right-s-line"></i>
          <span class="sce-bc-current">Equipment Details</span>
          @if (canViewMesData) {
            <button
              class="sce-bc-hash-btn"
              type="button"
              pTooltip="View Serial Number Data"
              tooltipPosition="right"
              (click)="openMesModal()">
              <i class="ri-hashtag"></i>
            </button>
          }
        </nav>
      } @else if (isProjectFormMode) {
        <h1 class="sce-skid-title">{{ getProjectFormDisplayTitle() }}</h1>
        <nav class="sce-breadcrumb">
          <button class="sce-bc-link" type="button" (click)="goBack()">Forms</button>
          <i class="ri-arrow-right-s-line"></i>
          <span class="sce-bc-current">Check Sheet</span>
        </nav>
      } @else {
        <h1 class="sce-skid-title">Check Sheet</h1>
        <nav class="sce-breadcrumb">
          <button class="sce-bc-link" type="button" (click)="goBack()">Equipment</button>
          <i class="ri-arrow-right-s-line"></i>
          <span class="sce-bc-current">Check Sheet</span>
        </nav>
      }
    </div>
    @if (!loading && templates.length > 0) {
      <div class="sce-page-hdr-right">
        @if (templates.length > 1) {
          <div class="sce-tabs-scroll" #tabsScroll>
            <div class="switch-tabs">
              @for (tmpl of templates; track trackByTemplateId($index, tmpl)) {
                <button
                  type="button"
                  [class.active]="activeTabIndex === $index"
                  [class.sce-tab--locked]="isTemplateLocked(tmpl.templateId)"
                  [attr.aria-disabled]="isTemplateLocked(tmpl.templateId)"
                  (click)="onTabChange($index.toString())"
                  [pTooltip]="getTemplateLockMessage(tmpl.templateId)"
                  tooltipPosition="top">
                  @if (isTemplateLocked(tmpl.templateId)) {
                    <i class="ri-lock-2-line"></i>
                  }
                  {{ tmpl.shortCode || tmpl.templateName }}
                </button>
              }
            </div>
          </div>
        }
        <button class="sce-upload-btn" type="button" (click)="openIntegrationUpload(integrationUploadInput)" [disabled]="isActiveTemplateReadOnly()">
          <i class="ri-upload-2-line"></i>Upload
        </button>
        <input #integrationUploadInput type="file" accept=".kisha,.json,application/json" hidden (change)="onIntegrationFileSelected($event)" />
      </div>
    }
  </div>
</div>

<!-- ===== Loading State ===== -->
@if (loading) {
  <div class="sce-loading">
    <i class="pi pi-spin pi-spinner text-4xl text-primary mb-3"></i>
    <p class="text-600">Loading checklist templates...</p>
  </div>
}

<!-- ===== Empty State ===== -->
@if (!loading && templates.length === 0) {
  <div class="sce-empty">
    <i class="pi pi-inbox text-6xl text-400 mb-3"></i>
    <p class="sce-empty-title">No Templates Assigned</p>
    <p class="sce-empty-desc">Please assign templates to this project first.</p>
  </div>
}

<!-- ===== Recursive Section Panel Template ===== -->
<ng-template #sectionPanel let-sect="section" let-level="level" let-tmpl="template">
  <div class="sce-panel" [class.sce-panel--sub]="level > 0" [class.sce-panel--open]="sect.isExpanded">

    <!-- Panel Header -->
    <div class="sce-panel-hdr" (click)="toggleSection(sect)">
      <button class="sce-toggle-btn" type="button" (click)="$event.stopPropagation(); toggleSection(sect)">
        <i [class]="sect.isExpanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'"></i>
      </button>
      <div class="sce-panel-info">
        <span class="sce-panel-name">{{ sect.sectionName }}</span>
        @if (sect.description) {
          <span class="sce-panel-desc" [class.sce-panel-desc--truncated]="!sect.isExpanded" [title]="!sect.isExpanded ? sect.description : null">{{ sect.description }}</span>
        }
      </div>
      <div class="sce-panel-acts" (click)="$event.stopPropagation()">
        <div class="sce-task-btn-wrapper">
        <button class="sce-icon-btn" type="button" title="Task Actions"
          (click)="prepareTaskMenu(sect, tmpl); sectionTaskMenu.toggle($event)">
          <i class="ri-list-check"></i>
        </button>
          @if (getSectionTaskCounts(sect.sectionId, tmpl.templateId).open > 0) {
            <span class="open-task-badge sce-task-badge">{{ getSectionTaskCounts(sect.sectionId, tmpl.templateId).open > 99 ? '99+' : getSectionTaskCounts(sect.sectionId, tmpl.templateId).open }}</span>
          }
        </div>
        <p-menu #sectionTaskMenu [model]="taskMenuItems" [popup]="true" appendTo="body"></p-menu>
      </div>
    </div>

    <!-- Panel Body (expanded) -->
    @if (sect.isExpanded) {
      <div class="sce-panel-body">

        <!-- Line Items / Controls -->
        @if (sect.lineItemList && sect.lineItemList.length > 0) {
          @for (lineItem of sect.lineItemList; track trackByLineItemId($index, lineItem)) {
            <div class="sce-line-item">
              @if (lineItem.controlTypeId !== undefined && lineItem.controlTypeId !== null) {
                @if (lineItem.controlTypeId === ControlType.TextBox) {
                  <app-textbox-control [lineItem]="lineItem"
                    [value]="getLineItemValue(lineItem.lineItemId)"
                    (valueChange)="setLineItemValue(lineItem.lineItemId, $event)"
                    [disabled]="isTemplateControlReadOnly(tmpl.templateId)"
                    [showError]="showErrors" />
                }
                @if (lineItem.controlTypeId === ControlType.TextArea) {
                  <app-textarea-control [lineItem]="lineItem"
                    [value]="getLineItemValue(lineItem.lineItemId)"
                    (valueChange)="setLineItemValue(lineItem.lineItemId, $event)"
                    [disabled]="isTemplateControlReadOnly(tmpl.templateId)"
                    [showError]="showErrors" />
                }
                @if (lineItem.controlTypeId === ControlType.CheckBox) {
                  <app-checkbox-control [lineItem]="lineItem"
                    [value]="getLineItemValue(lineItem.lineItemId)"
                    (valueChange)="setLineItemValue(lineItem.lineItemId, $event)"
                    [disabled]="isTemplateControlReadOnly(tmpl.templateId)"
                    [showError]="showErrors" />
                }
                @if (lineItem.controlTypeId === ControlType.Select) {
                  <app-select-control [lineItem]="lineItem"
                    [options]="lineItem.lineItemOptionValueList || []"
                    [value]="getLineItemValue(lineItem.lineItemId)"
                    (valueChange)="setLineItemValue(lineItem.lineItemId, $event)"
                    [disabled]="isTemplateControlReadOnly(tmpl.templateId)"
                    [showError]="showErrors" />
                }
                @if (lineItem.controlTypeId === ControlType.ImpactedArea) {
                  <app-select-control [lineItem]="lineItem"
                    [options]="lineItem.lineItemOptionValueList || []"
                    [multiple]="true"
                    [value]="getLineItemValue(lineItem.lineItemId)"
                    (valueChange)="setLineItemValue(lineItem.lineItemId, $event)"
                    [disabled]="isTemplateControlReadOnly(tmpl.templateId)"
                    [showError]="showErrors" />
                }
                @if (lineItem.controlTypeId === ControlType.DatePicker) {
                  <app-datepicker-control [lineItem]="lineItem"
                    [value]="getLineItemValue(lineItem.lineItemId)"
                    (valueChange)="setLineItemValue(lineItem.lineItemId, $event)"
                    [disabled]="isTemplateControlReadOnly(tmpl.templateId)"
                    [showError]="showErrors" />
                }
                @if (lineItem.controlTypeId === ControlType.DateTimePicker) {
                  <app-datetimepicker-control [lineItem]="lineItem"
                    [value]="getLineItemValue(lineItem.lineItemId)"
                    (valueChange)="setLineItemValue(lineItem.lineItemId, $event)"
                    [disabled]="isTemplateControlReadOnly(tmpl.templateId)"
                    [showError]="showErrors" />
                }
                @if (lineItem.controlTypeId === ControlType.Table) {
                  <app-table-control [lineItem]="lineItem"
                    [projectId]="projectId"
                    [value]="getLineItemValue(lineItem.lineItemId)"
                    (valueChange)="setLineItemValue(lineItem.lineItemId, $event)"
                    [disabled]="isTemplateControlReadOnly(tmpl.templateId)"
                    [showError]="showErrors" />
                }
                @if (lineItem.controlTypeId === ControlType.Attachment) {
                  <app-attachment-control [lineItem]="lineItem"
                    [value]="getLineItemValue(lineItem.lineItemId)"
                    (valueChange)="setLineItemValue(lineItem.lineItemId, $event)"
                    [existingFilesData]="existingFilesMap[lineItem.lineItemId] || []"
                    [disabled]="isTemplateControlReadOnly(tmpl.templateId)"
                    [showError]="showErrors" />
                }
                @if (lineItem.controlTypeId === ControlType.Picture) {
                  <app-picture-control [lineItem]="lineItem"
                    [value]="getLineItemValue(lineItem.lineItemId)"
                    (valueChange)="setLineItemValue(lineItem.lineItemId, $event)"
                    [projectId]="projectId"
                    [existingFilesData]="existingFilesMap[lineItem.lineItemId] || []"
                    [disabled]="isTemplateControlReadOnly(tmpl.templateId)"
                    [showError]="showErrors" />
                }
                @if (lineItem.controlTypeId === ControlType.Signature) {
                  <app-signature-control [lineItem]="lineItem"
                    [value]="getLineItemValue(lineItem.lineItemId)"
                    (valueChange)="setLineItemValue(lineItem.lineItemId, $event)"
                    [existingFilesData]="existingFilesMap[lineItem.lineItemId] || []"
                    [disabled]="isTemplateControlReadOnly(tmpl.templateId)"
                    [showError]="showErrors" />
                }
                @if (lineItem.controlTypeId === ControlType.DigitalSignature) {
                  <app-digitalsignature-control [lineItem]="lineItem"
                    [value]="getLineItemValue(lineItem.lineItemId)"
                    (valueChange)="setLineItemValue(lineItem.lineItemId, $event)"
                    [disabled]="isTemplateControlReadOnly(tmpl.templateId)"
                    [showError]="showErrors" />
                }
                @if (lineItem.controlTypeId === ControlType.QRScanner) {
                  <app-qrscanner-control [lineItem]="lineItem"
                    [value]="getLineItemValue(lineItem.lineItemId)"
                    (valueChange)="setLineItemValue(lineItem.lineItemId, $event)"
                    [disabled]="isTemplateControlReadOnly(tmpl.templateId)"
                    [showError]="showErrors" />
                }
                @if (lineItem.controlTypeId === ControlType.Weather) {
                  <app-weather-control [lineItem]="lineItem"
                    [value]="getLineItemValue(lineItem.lineItemId)"
                    [city]="projectData?.customerCity || ''"
                    [state]="projectData?.customerState || ''"
                    [zip]="projectData?.customerZip || ''"
                    [latitude]="projectData?.latitude || ''"
                    [longitude]="projectData?.longitude || ''"
                    [date]="getTemplateStatusDate(tmpl.templateId)"
                    (valueChange)="setLineItemValue(lineItem.lineItemId, $event)"
                    [disabled]="isTemplateControlReadOnly(tmpl.templateId)"
                    [showError]="showErrors" />
                }
                @if (lineItem.controlTypeId === ControlType.RichText) {
                  <app-richtext-control [lineItem]="lineItem"
                    [value]="getLineItemValue(lineItem.lineItemId)"
                    (valueChange)="setLineItemValue(lineItem.lineItemId, $event)"
                    [disabled]="isTemplateControlReadOnly(tmpl.templateId)"
                    [showError]="showErrors" />
                }
                @if (lineItem.controlTypeId === ControlType.LabelText) {
                  <app-labeltext-control [lineItem]="lineItem" />
                }
                @if (lineItem.controlTypeId === ControlType.Tasks) {
                  <app-tasks-control [lineItem]="lineItem"
                    [projectId]="projectId"
                    [value]="getLineItemValue(lineItem.lineItemId)"
                    (valueChange)="setLineItemValue(lineItem.lineItemId, $event)"
                    [disabled]="isTemplateControlReadOnly(tmpl.templateId)"
                    [showError]="showErrors" />
                }
                @if (lineItem.controlTypeId === ControlType.Integer) {
                  <app-integer-control [lineItem]="lineItem"
                    [value]="getLineItemValue(lineItem.lineItemId)"
                    (valueChange)="setLineItemValue(lineItem.lineItemId, $event)"
                    [disabled]="isTemplateControlReadOnly(tmpl.templateId)"
                    [showError]="showErrors" />
                }
                @if (lineItem.controlTypeId === ControlType.Decimal) {
                  <app-decimal-control [lineItem]="lineItem"
                    [value]="getLineItemValue(lineItem.lineItemId)"
                    (valueChange)="setLineItemValue(lineItem.lineItemId, $event)"
                    [disabled]="isTemplateControlReadOnly(tmpl.templateId)"
                    [showError]="showErrors" />
                }
                @if (lineItem.controlTypeId === ControlType.Users) {
                  <app-users-control [lineItem]="lineItem"
                    [projectId]="projectId"
                    [value]="getLineItemValue(lineItem.lineItemId)"
                    (valueChange)="setLineItemValue(lineItem.lineItemId, $event)"
                    [disabled]="isTemplateControlReadOnly(tmpl.templateId)"
                    [showError]="showErrors" />
                }
                @if (lineItem.controlTypeId === ControlType.Skids) {
                  <app-skids-control [lineItem]="lineItem"
                    [projectId]="projectId"
                    [value]="getLineItemValue(lineItem.lineItemId)"
                    (valueChange)="setLineItemValue(lineItem.lineItemId, $event)"
                    [disabled]="isTemplateControlReadOnly(tmpl.templateId)"
                    [showError]="showErrors" />
                }
                @if (lineItem.controlTypeId === ControlType.Judgement) {
                  <app-judgement-control [lineItem]="lineItem"
                    [value]="getLineItemValue(lineItem.lineItemId)"
                    (valueChange)="setLineItemValue(lineItem.lineItemId, $event)"
                    [disabled]="isTemplateControlReadOnly(tmpl.templateId)"
                    [showError]="showErrors" />
                }
              }
            </div>
          }
        }

        <!-- Subsections -->
        @if (sect.subsections && sect.subsections.length > 0) {
          <div class="sce-subsections">
            @for (subsect of sect.subsections; track trackBySectionId($index, subsect)) {
              <ng-container *ngTemplateOutlet="sectionPanel; context: {section: subsect, level: level + 1, template: tmpl}"></ng-container>
            }
          </div>
        }

      </div>
    }
  </div>
</ng-template>

<!-- ===== Main Content ===== -->
@if (!loading && templates.length > 0) {
  @let activeTemplate = templates[activeTabIndex];
  <div class="sce-content">

    <!-- Sections Column -->
    <div class="sce-sections-col">
      @if (activeTemplate.sections && activeTemplate.sections.length > 0) {
        @for (section of activeTemplate.sections; track trackBySectionId($index, section)) {
          <ng-container *ngTemplateOutlet="sectionPanel; context: {section: section, level: 0, template: activeTemplate}"></ng-container>
        }
      } @else {
        <div class="sce-empty-sections">
          <p>No sections found in this template.</p>
        </div>
      }
    </div>

    <!-- Details Panel -->
    <div class="sce-details-panel">
      <div class="sce-details-hdr">
        <div class="sce-details-title-wrap">
          <h3 class="sce-details-title">Details</h3>
          @if (getTemplateOpenTaskCount(activeTemplate.templateId) > 0) {
            <span class="open-task-badge sce-task-badge sce-details-task-badge">
              {{ getTemplateOpenTaskCount(activeTemplate.templateId) > 99 ? '99+' : getTemplateOpenTaskCount(activeTemplate.templateId) }}
            </span>
          }
        </div>
        <button class="sce-export-btn" type="button" (click)="onDownloadPdf(activeTemplate)">
          <i class="ri-download-line"></i>Export PDF
        </button>
      </div>
      <div class="sce-details-divider"></div>

      <div class="status-field">
        <label>Status</label>
        @if (isStatusFieldVisible(activeTemplate.templateId)) {
          <p-select [options]="getStatusOptionsForTemplate(activeTemplate.templateId)"
            [ngModel]="getSelectedStatusValue(activeTemplate.templateId)"
            (onChange)="onStatusFieldChange(activeTemplate.templateId, 'status', $event.value)"
            optionLabel="label" optionValue="value" placeholder="Select Status"
            styleClass="w-full" [disabled]="saving || !canEditStatusField(activeTemplate.templateId)" appendTo="body" />
        }
      </div>

      @if (showOverrideCheckbox(activeTemplate.templateId)) {
        <label class="sce-override-toggle">
          <input
            type="checkbox"
            [checked]="overrideSelection[activeTemplate.templateId]"
            [disabled]="isOverridePersisted(activeTemplate.templateId)"
            (change)="onOverrideToggle(activeTemplate.templateId, $any($event.target).checked)" />
          <span>Override next template unlock</span>
        </label>
      }

      <div class="status-field">
        <label>Assignee <span class="required-star">*</span></label>
        <p-select [options]="projectUsers"
          [ngModel]="getTemplateStatus(activeTemplate.templateId).assigneedBy"
          (onChange)="onStatusFieldChange(activeTemplate.templateId, 'assigneedBy', $event.value)"
          optionLabel="label" optionValue="value" placeholder="Select Assignee"
          styleClass="w-full" [disabled]="saving || !canEditTemplateDetails(activeTemplate.templateId)" appendTo="body"
          [class.ng-invalid]="showErrors && !getTemplateStatus(activeTemplate.templateId).assigneedBy"
          [class.ng-dirty]="showErrors && !getTemplateStatus(activeTemplate.templateId).assigneedBy" />
      </div>

      <div class="status-field">
        <label>Date <span class="required-star">*</span></label>
        <p-datepicker [ngModel]="getTemplateStatus(activeTemplate.templateId).date"
          (onSelect)="onStatusFieldChange(activeTemplate.templateId, 'date', $event)"
          dateFormat="mm/dd/yy" [fluid]="true" [showIcon]="true"
          iconDisplay="input" [disabled]="saving || !canEditTemplateDetails(activeTemplate.templateId)" appendTo="body"
          [class.ng-invalid]="showErrors && !getTemplateStatus(activeTemplate.templateId).date"
          [class.ng-dirty]="showErrors && !getTemplateStatus(activeTemplate.templateId).date" />
      </div>

      <div class="status-field">
        <label>Due Date <span class="required-star">*</span></label>
        <p-datepicker [ngModel]="getTemplateStatus(activeTemplate.templateId).duedate"
          (onSelect)="onStatusFieldChange(activeTemplate.templateId, 'duedate', $event)"
          dateFormat="mm/dd/yy" [fluid]="true" [showIcon]="true"
          iconDisplay="input" [disabled]="saving || !canEditTemplateDetails(activeTemplate.templateId)" appendTo="body"
          [class.ng-invalid]="showErrors && !getTemplateStatus(activeTemplate.templateId).duedate"
          [class.ng-dirty]="showErrors && !getTemplateStatus(activeTemplate.templateId).duedate" />
      </div>

      @if (getTemplateLastUpdatedText(activeTemplate.templateId); as lastUpdatedText) {
        <div class="status-last-updated" aria-label="Last updated">
          <span class="status-last-updated__label">{{ getTemplateAuditLabel(activeTemplate.templateId) }}</span>
          <span>{{ lastUpdatedText }}</span>
        </div>
      }

    </div>

  </div>
}

<!-- ===== MES Serial Numbers Modal ===== -->
<app-mes-serial-numbers-modal
  [visible]="showMesModal"
  [blockScheduleId]="blockScheduleId"
  (visibleChange)="showMesModal = $event">
</app-mes-serial-numbers-modal>

<!-- ===== Task Form Dialog ===== -->
<app-task-form
  [visible]="showTaskFormDialog"
  [projectId]="projectId"
  [blockScheduleId]="blockScheduleId"
  [sectionId]="selectedSectionForTask?.sectionId"
  [projectTemplateId]="selectedTemplateForTask?.templateId"
  [editTask]="editTaskForDialog"
  [isProjectFormMode]="isProjectFormMode"
  (visibleChange)="onTaskFormClose()"
  (taskSubmit)="onTaskSubmit($event)">
</app-task-form>

<!-- ===== View Tasks Dialog ===== -->
<p-dialog
  [header]="'Tasks for: ' + (selectedSectionForTask?.sectionName || '')"
  [(visible)]="showViewTasksDialog"
  [modal]="true"
  [style]="{width: '700px', maxHeight: '80vh'}"
  [closable]="true"
  (onHide)="onViewTasksDialogClose()">

  @if (loadingTasks) {
    <div class="flex justify-content-center align-items-center py-4">
      <i class="pi pi-spin pi-spinner text-2xl text-primary"></i>
      <span class="ml-2">Loading tasks...</span>
    </div>
  } @else if (sectionTasks.length === 0) {
    <div class="text-center py-4">
      <i class="pi pi-inbox text-4xl text-400 mb-2"></i>
      <p class="text-600">No tasks found for this section.</p>
      <button type="button" class="ctrl-action-btn"
        (click)="showViewTasksDialog = false; openCreateTaskDialog(selectedSectionForTask, selectedTemplateForTask!)"
        [disabled]="isReadOnly">
        <i class="ri-add-line"></i>Create Task
      </button>
    </div>
  } @else {
    <div class="task-list">
      @for (task of sectionTasks; track task.taskId) {
        <div class="task-item p-3 border-bottom-1 surface-border hover:surface-100 cursor-pointer"
          (click)="isReadOnly ? null : openEditTaskDialog(task)">
          <div class="flex align-items-start justify-content-between">
            <div class="flex-1">
              <div class="font-medium text-900">{{ task.title }}</div>
              @if (task.description) {
                <div class="text-sm text-600 mt-1">{{ task.description }}</div>
              }
              <div class="flex align-items-center gap-2 mt-2">
                @if (task.statusName) {
                  <span class="text-xs px-2 py-1 border-round"
                    [style.background-color]="task.statusColor || '#e0e0e0'"
                    [style.color]="'#fff'">
                    {{ task.statusName }}
                  </span>
                }
                @if (task.assignedToName) {
                  <span class="text-xs text-600">
                    <i class="pi pi-user mr-1"></i>{{ task.assignedToName }}
                  </span>
                }
                @if (task.endDate) {
                  <span class="text-xs text-600">
                    <i class="pi pi-calendar mr-1"></i>{{ formatTaskDate(task.endDate) }}
                  </span>
                }
              </div>
            </div>
          </div>
        </div>
      }
    </div>
  }
</p-dialog>

<!-- ===== Page Footer ===== -->
<div class="sticky-footer sce-footer">
  <button type="button" class="sce-footer-back" (click)="goBack()" [disabled]="loading || saving">
    <i class="ri-arrow-left-line"></i>
    {{ isProjectFormMode ? 'Back to Forms' : 'Back to Equipment' }}
  </button>
  <div class="save-button-container">
    <button type="button" class="sce-footer-primary" (click)="onUpdateAndCloseClick($event)"
      [disabled]="loading || saving || isActiveTemplateReadOnly()" #menuButton>
      <i [class]="saving ? 'ri-loader-4-line ctrl-spin' : 'ri-check-line'"></i>
      {{ saving ? 'Saving...' : 'Update and Close' }}
    </button>
    <p-menu #menu [model]="updateMenuItems" [popup]="true" appendTo="body"></p-menu>
  </div>
</div>
